import React, { type JSX } from 'react';

type Variant =
  | 'primary'
  | 'secondary'
  | 'tertiary'
  | 'danger'
  | 'success'
  | 'info'
  | 'danger-light'
  | 'quaternary'
  | 'antiwaste';

type Weight = 'bold' | 'base' | 'light';

type Radius = 'none' | 'xs' | 'small' | 'medium' | 'large';

type Size = 'small' | 'medium' | 'large';

type IVariant = {
  [variant in Variant]: string;
};

type IWeight = {
  [weight in Weight]: string;
};

type IRadius = {
  [radius in Radius]: string | number;
};

type ISize = {
  [size in Size]: string;
};

interface ITag {
  variant?: Variant;
  hasBorderRadius?: boolean;
  weight?: Weight;
  size?: 'small' | 'large';
  radius?: Radius;
  fontSize?: Size;
  children: React.ReactNode;
}

const themePadding = {
  small: '2px 4px',
  large: '10px 14px',
};

const themeRadius: IRadius = {
  none: 0,
  xs: 'var(--rd-1)',
  small: 'var(--rd-2)',
  medium: 'var(--rd-3)',
  large: 'var(--rd-4)',
};

const themeBg: IVariant = {
  primary: 'var(--clr-primary-1)',
  secondary: 'var(--clr-secondary-1)',
  tertiary: 'var(--clr-tertiary-1)',
  quaternary: 'var(--clr-quaternary-3)',
  danger: 'var(--clr-danger)',
  success: 'var(--clr-quaternary-2)',
  info: 'var(--clr-primary-4)',
  'danger-light': 'var(--clr-secondary-3)',
  antiwaste: 'var(--clr-secondary-4)',
};

const themeColor: IVariant = {
  primary: 'var(--black)',
  secondary: 'var(--white)',
  tertiary: 'var(--white)',
  quaternary: 'var(--black)',
  danger: 'var(--white)',
  success: 'var(--black)',
  info: 'var(--black)',
  'danger-light': 'var(--black)',
  antiwaste: 'var(--clr-secondary-1)',
};

const themeWeight: IWeight = {
  light: 'var(--font-weight-light)',
  base: 'var(--font-weight-base)',
  bold: 'var(--font-weight-bold)',
};

const themeSize: ISize = {
  small: 'var(--font-size-1)',
  medium: 'var(--font-size-1-5)',
  large: 'var(--font-size-2)',
};

/**
 * @deprecated Use css utils .tw-lf-discount or .tw-lf-tag instead
 */
const Tag = ({
  children,
  variant = 'primary',
  size = 'small',
  weight = 'base',
  radius = 'medium',
  fontSize = 'large',
}: ITag): JSX.Element => {
  return (
    <>
      <span className="tag">{children}</span>
      <style jsx>{`
        .tag {
          color: ${variant && themeColor[variant]};
          display: inline-block;
          border-radius: ${radius && themeRadius[radius]};
          padding: ${size && themePadding[size]};
          pointer-events: none;
          background-color: ${variant && themeBg[variant]};
          font-size: ${fontSize && themeSize[fontSize]};
          font-weight: ${weight && themeWeight[weight]};
        }

        @media (max-width: 800px) {
          .tag {
            font-size: 16px;
          }
        }
      `}</style>
    </>
  );
};

export default Tag;
